import React, { useState, useMemo } from 'react'
import { compose } from 'redux'
import { lpForm } from 'lp-form'
import { Field, propTypes as formPropTypes } from 'redux-form'
import {
  CloudinaryFileInput,
  CloudinaryMultiFileInput,
  RemovableThumbnail,
  RemovableFile,
  CheckboxGroup,
} from 'components'
import {
  ButtonArea,
  SubmitButton,
  Input,
  Select,
  Textarea,
} from 'lp-components'
import { CLOUD_RESIZE_PRESET } from 'config'
import {
  useCommunity,
  formatObjectsToIds,
  parseIdsToObjects,
  serializeOptions,
  convertCloudinarySrcUrlToFilename,
} from 'utils'
import * as Types from 'types'
import PropTypes from 'prop-types'
import { withProps } from 'recompose'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline),
  demographics: PropTypes.arrayOf(Types.demographic),
  onSubmit: PropTypes.func,
}

const APPLICATION_CONFIG_KEY = 'teachingArtistApplication'
const FIELDS_CONFIG_KEY = 'fields'

const buildConstraints = (community) => {
  const constraints = {}
  const fields = community?.[APPLICATION_CONFIG_KEY]?.[FIELDS_CONFIG_KEY] || {}

  Object.keys(fields).forEach((fieldName) => {
    const fieldConfig = fields[fieldName]
    if (!fieldConfig.visible) return

    constraints[fieldName] = {
      presence: fieldConfig.required ? true : false,
    }
    switch (fieldName) {
      case 'email':
        constraints[fieldName].email = true
        break
      case 'phoneNumber':
        if (fieldConfig.required) {
          constraints[fieldName].format = {
            pattern: /^\(\d{3}\)\s\d{3}-\d{4}$/,
            message: 'must be in format (xxx) xxx-xxxx',
          }
        }
        break
      case 'address':
        // Allow an empty/whitespace-only value so a blank required field only
        // shows the presence ("can't be blank") error. validate.js's format
        // validator only skips `undefined`, so without the `^\s*$` alternative a
        // single space would fail both presence AND format.
        constraints[fieldName].format = {
          pattern:
            /^\s*$|^\d+\s+[\w\s.,]+,\s*[\w\s]+,\s*[A-Z]{2}\s+\d{5}(-\d{4})?$/,
          message:
            'must be a valid address (e.g., "123 Main St, Chicago, IL 60601")',
        }
        break
      default:
        break
    }
  })
  return constraints
}

const isFieldVisible = (community, fieldName) => {
  const fields = community?.[APPLICATION_CONFIG_KEY]?.[FIELDS_CONFIG_KEY] || {}
  return fields[fieldName]?.visible !== false
}

const isFieldRequired = (community, fieldName) => {
  const fields = community?.[APPLICATION_CONFIG_KEY]?.[FIELDS_CONFIG_KEY] || {}
  return fields[fieldName]?.required === true
}

const getFieldLabel = (community, fieldName, baseLabel) => {
  const required = isFieldRequired(community, fieldName)
  return required ? `${baseLabel} *` : baseLabel
}

const filterRacialDemographics = (demographics) => {
  if (!demographics) return []
  const nonRacialIds = [7, 5, 6]
  return demographics.filter(
    (demographic) => !nonRacialIds.includes(demographic.id)
  )
}

const filterNoneDiscipline = (disciplines) => {
  if (!disciplines) return []
  return disciplines.filter(
    (discipline) =>
      String(
        discipline?.displayName || discipline?.name || ''
      ).toLowerCase() !== 'none'
  )
}

function ApplicationFormStep1({
  change,
  error,
  handleSubmit,
  submitting,
  disciplines,
  demographics,
  onSubmit,
}) {
  const community = useCommunity()
  const [avatarImage, setAvatarImage] = useState(null)
  const initialValuesId = 'initialValuesId'
  const milliseconds = Date.now()
  const uploadSuffix = `${initialValuesId}/${milliseconds}/`

  return (
    <form
      className="application-form"
      onSubmit={onSubmit ? handleSubmit(onSubmit) : handleSubmit}
      noValidate
    >
      {error && (
        <p className="full-error-message" role="alert">
          {error}
        </p>
      )}

      {(isFieldVisible(community, 'firstName') ||
        isFieldVisible(community, 'lastName')) && (
        <div className="grid-container col-2">
          {isFieldVisible(community, 'firstName') && (
            <Field
              name="firstName"
              type="text"
              label={getFieldLabel(community, 'firstName', 'First Name')}
              placeholder="First Name"
              component={Input}
              autoComplete="given-name"
            />
          )}
          {isFieldVisible(community, 'lastName') && (
            <Field
              name="lastName"
              type="text"
              label={getFieldLabel(community, 'lastName', 'Last Name')}
              placeholder="Last Name"
              component={Input}
              autoComplete="family-name"
            />
          )}
        </div>
      )}

      {isFieldVisible(community, 'email') && (
        <Field
          name="email"
          type="email"
          label={getFieldLabel(community, 'email', 'Email')}
          placeholder="Email Address"
          component={Input}
          autoComplete="email"
        />
      )}

      {isFieldVisible(community, 'address') && (
        <div className="label-container">
          <label>
            Address
            {isFieldRequired(community, 'address') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">
            Address must be typed with the following formatting #### Street
            Name, Sacramento, CA #####. No extra spaces or periods can be
            entered. Commas must be entered in correct place.
          </span>
          <Field
            name="address"
            type="text"
            label={false}
            placeholder="123 Main St, Sacramento, CA 95814"
            component={Input}
            autoComplete="street-address"
          />
        </div>
      )}

      {isFieldVisible(community, 'phoneNumber') && (
        <div className="label-container">
          <label>
            Phone Number
            {isFieldRequired(community, 'phoneNumber') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">
            Phone numbers must be entered in the following format: (###)
            ###-####. Do not leave empty spaces at the end of the phone number.
          </span>
          <Field
            name="phoneNumber"
            type="tel"
            label={false}
            placeholder="(555) 555-5555"
            component={Input}
            autoComplete="tel"
          />
        </div>
      )}

      {isFieldVisible(community, 'artistBiography') && (
        <Field
          name="artistBiography"
          label={getFieldLabel(
            community,
            'artistBiography',
            'Artist Biography'
          )}
          placeholder="Artist Biography"
          component={Textarea}
          rows={6}
        />
      )}

      {isFieldVisible(community, 'disciplines') && (
        <div className="label-container">
          <label>
            Which arts disciplines are addressed by your school programs?
            {isFieldRequired(community, 'disciplines') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">Select all that apply.</span>
          <div className="floating-checkboxes">
            <Field
              name="disciplines"
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('id')}
              parse={parseIdsToObjects('id')}
              options={serializeOptions(filterNoneDiscipline(disciplines), {
                key: 'displayName',
                value: 'id',
              })}
            />
          </div>
        </div>
      )}

      {isFieldVisible(community, 'website') && (
        <Field
          name="website"
          type="text"
          label={getFieldLabel(community, 'website', 'Website')}
          placeholder="https://"
          component={Input}
          autoComplete="url"
        />
      )}

      {isFieldVisible(community, 'demographic') && (
        <div className="label-container">
          <label>
            Racial Demographics
            {isFieldRequired(community, 'demographic') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">
            Note this section is regarding the racial demographics of the
            teaching artist and is optional to submit.
          </span>
          <Field
            name="demographic"
            placeholder="Select"
            component={Select}
            options={serializeOptions(filterRacialDemographics(demographics), {
              key: 'displayName',
              value: 'id',
            })}
            label={false}
            aria-label="Racial Demographics"
            disabled={false}
          />
        </div>
      )}

      {isFieldVisible(community, 'profilePicture') && (
        <div className="label-container application-upload-group">
          <label>
            Profile Picture or Logo
            {isFieldRequired(community, 'profilePicture') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-upload-hint">
            (Accepted formats include png, jpg and jpeg. Your file must be less
            than 10 MB and will be cropped to be a square when uploaded)
          </span>
          <Field
            name="profilePicture"
            accept=".png,.jpg,.jpeg"
            component={CloudinaryFileInput}
            uploadPath={`/artists/users/profile-pictures/${uploadSuffix}`}
            uploadPreset={CLOUD_RESIZE_PRESET}
            onUploadSuccess={({ secureUrl }) => {
              const filename = convertCloudinarySrcUrlToFilename(secureUrl)
              setAvatarImage({ src: secureUrl, filename })
            }}
            previewComponent={RemovableThumbnail}
            remove={() => {
              change('profilePicture', '')
              setAvatarImage(null)
            }}
            image={avatarImage}
            className="col-4"
          />
        </div>
      )}

      {isFieldVisible(community, 'spotlightVideo') && (
        <div className="label-container application-upload-group">
          <label>
            Spotlight Video
            {isFieldRequired(community, 'spotlightVideo') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-upload-hint">
            Upload a video that showcases your programming in action in school
            or classroom settings. (Accepted formats include mp4. Your file must
            be less than 500 MB). Upload photos and videos only, do not attempt
            to drag and drop media.
          </span>
          <Field
            name="spotlightVideo"
            accept=".mp4"
            component={CloudinaryFileInput}
            uploadPath={`/artists/users/spotlight-video/${uploadSuffix}`}
            onUploadSuccess={() => {}}
            previewComponent={RemovableFile}
            remove={() => change('spotlightVideo', '')}
          />
        </div>
      )}

      {isFieldVisible(community, 'spotlightImages') && (
        <div className="label-container application-upload-group">
          <label>
            Spotlight Images
            {isFieldRequired(community, 'spotlightImages') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-upload-hint">
            Upload photos that showcase your programming in action in school or
            classroom settings. (Accepted formats include png, jpg and jpeg.
            Your file must be less than 10 MB and will be cropped to be a square
            when uploaded)
          </span>
          <Field
            name="spotlightImages"
            component={CloudinaryMultiFileInput}
            uploadPath={`/artists/users/spotlight-images/${uploadSuffix}`}
            uploadPreset={CLOUD_RESIZE_PRESET}
            accept=".png,.jpg,.jpeg"
            onUploadSuccess={() => {}}
          />
        </div>
      )}

      <ButtonArea>
        <SubmitButton
          className="full-width button-secondary"
          submitting={submitting}
        >
          Next
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

ApplicationFormStep1.propTypes = propTypes

const FormWithConstraints = compose(
  withProps(() => ({
    beforeSubmit: ({ disciplines, ...values }) => ({
      ...values,
      disciplines: formatObjectsToIds('id', disciplines),
    }),
  })),
  lpForm({
    name: 'teaching-artist-application-step1',
    constraints: {},
    initialValues: { spotlightImages: [] },
  })
)(ApplicationFormStep1)

function ApplicationFormStep1WithConfig(props) {
  const community = useCommunity()
  const constraints = useMemo(() => buildConstraints(community), [community])
  return <FormWithConstraints constraints={constraints} {...props} />
}

export default ApplicationFormStep1WithConfig
